import ExcelJS from 'exceljs'

import { descargarBuffer } from './exportarPos'
import { formatearFechaPos, parsearFechaLocalPos } from './pos'

const FORMATO_MONTO = '"$"#,##0.##'
const FORMATO_PCT = '0.0"%"'
const FORMATO_FECHA = 'dd-mm-yyyy'
const FORMATO_KG = '#,##0.000'

function agregarHojaMetricas(libro, titulo, filas) {
  const hoja = libro.addWorksheet(titulo)

  hoja.columns = [
    { header: 'Métrica', key: 'metrica', width: 34 },
    { header: 'Valor', key: 'valor', width: 20 },
  ]

  hoja.getRow(1).font = { bold: true }

  for (const fila of filas) {
    if (fila.seccion) {
      const filaSeccion = hoja.addRow({ metrica: fila.seccion, valor: '' })
      filaSeccion.font = { bold: true }
      continue
    }

    const filaAgregada = hoja.addRow({ metrica: fila.etiqueta, valor: fila.valor })

    if (fila.formato) {
      filaAgregada.getCell('valor').numFmt = fila.formato
    }
  }

  return hoja
}

// Hoja "Resumen": período, filtros aplicados y todas las métricas
// generales/cobranza/descuentos/medios de pago/envases/top listas.
function construirFilasResumen(resumen, filtrosTexto) {
  if (!resumen) return []

  const g = resumen.generales
  const filas = [
    { seccion: 'Período' },
    { etiqueta: 'Desde', valor: parsearFechaLocalPos(resumen.periodo.desde), formato: FORMATO_FECHA },
    { etiqueta: 'Hasta', valor: parsearFechaLocalPos(resumen.periodo.hasta), formato: FORMATO_FECHA },
    { etiqueta: 'Filtros aplicados', valor: filtrosTexto || 'Ninguno' },

    { seccion: 'Generales' },
    { etiqueta: 'Total vendido', valor: g.total_vendido, formato: FORMATO_MONTO },
    { etiqueta: 'Subtotal vendido', valor: g.subtotal_vendido, formato: FORMATO_MONTO },
    { etiqueta: 'Total descuentos', valor: g.total_descuentos, formato: FORMATO_MONTO },
    { etiqueta: 'Cantidad de ventas', valor: g.cantidad_ventas },
    { etiqueta: 'Kilos vendidos', valor: g.kilos_vendidos, formato: FORMATO_KG },
    { etiqueta: 'Clientes con compras', valor: g.clientes_con_compras },
    { etiqueta: 'Clientes nuevos', valor: g.clientes_nuevos },
    { etiqueta: 'Clientes recurrentes', valor: g.clientes_recurrentes },
    { etiqueta: 'Ticket promedio', valor: g.ticket_promedio, formato: FORMATO_MONTO },
    { etiqueta: 'Ticket mediano', valor: g.ticket_mediano, formato: FORMATO_MONTO },
    { etiqueta: 'Venta promedio por cliente', valor: g.venta_promedio_cliente, formato: FORMATO_MONTO },
    { etiqueta: 'Kilos promedio por venta', valor: g.kilos_promedio_venta, formato: FORMATO_KG },
    { etiqueta: 'Kilos promedio por cliente', valor: g.kilos_promedio_cliente, formato: FORMATO_KG },
    { etiqueta: 'Precio promedio/kg', valor: g.precio_promedio_kg, formato: FORMATO_MONTO },
    { etiqueta: 'Ventas anuladas', valor: g.ventas_anuladas },
    { etiqueta: 'Monto anulado', valor: g.monto_anulado, formato: FORMATO_MONTO },

    { seccion: 'Cobranza' },
    { etiqueta: 'Monto pagado', valor: g.monto_pagado, formato: FORMATO_MONTO },
    { etiqueta: 'Saldo pendiente', valor: g.saldo_pendiente, formato: FORMATO_MONTO },
    { etiqueta: '% cobrado', valor: g.pct_cobrado, formato: FORMATO_PCT },
    { etiqueta: '% pendiente', valor: g.pct_pendiente, formato: FORMATO_PCT },
    { etiqueta: 'Ventas pagadas', valor: g.ventas_pagadas },
    { etiqueta: 'Ventas parciales', valor: g.ventas_parciales },
    { etiqueta: 'Ventas pendientes', valor: g.ventas_pendientes },
    { etiqueta: 'Saldo promedio pendiente', valor: resumen.cobranza.saldo_promedio_pendiente, formato: FORMATO_MONTO },
    { etiqueta: 'Mayor saldo pendiente', valor: resumen.cobranza.mayor_saldo_pendiente, formato: FORMATO_MONTO },

    { seccion: 'Descuentos' },
    { etiqueta: 'Ventas con descuento', valor: resumen.descuentos.cantidad_ventas_con_descuento },
    { etiqueta: 'Descuento promedio', valor: resumen.descuentos.descuento_promedio, formato: FORMATO_MONTO },
    { etiqueta: 'Mayor descuento', valor: resumen.descuentos.mayor_descuento, formato: FORMATO_MONTO },
    { etiqueta: 'Impacto sobre subtotal', valor: resumen.descuentos.impacto_pct_sobre_subtotal, formato: FORMATO_PCT },

    { seccion: 'Cajas / Rejillas' },
    { etiqueta: 'Ventas pagadas', valor: resumen.envases.ventas_pagadas },
    { etiqueta: 'Ventas pendientes', valor: resumen.envases.ventas_pendientes },
    { etiqueta: 'Monto pagado', valor: resumen.envases.monto_pagado, formato: FORMATO_MONTO },

    { seccion: 'Concentración' },
    { etiqueta: 'Top 5 clientes (% del total)', valor: resumen.concentracion.top5_clientes_pct, formato: FORMATO_PCT },
    { etiqueta: 'Top 10 clientes (% del total)', valor: resumen.concentracion.top10_clientes_pct, formato: FORMATO_PCT },
    { etiqueta: 'Top 5 productos (% monto)', valor: resumen.concentracion.top5_productos_monto_pct, formato: FORMATO_PCT },
    { etiqueta: 'Top 5 productos (% kilos)', valor: resumen.concentracion.top5_productos_kilos_pct, formato: FORMATO_PCT },
  ]

  if (resumen.medios_pago.length > 0) {
    filas.push({ seccion: 'Medios de pago' })
    for (const medio of resumen.medios_pago) {
      filas.push({ etiqueta: medio.medio_pago, valor: medio.monto, formato: FORMATO_MONTO })
    }
  }

  return filas
}

const COLUMNAS_PRODUCTOS = [
  { encabezado: 'Producto', clave: 'producto', ancho: 28 },
  { encabezado: 'Código', clave: 'codigo', ancho: 14 },
  { encabezado: 'Ventas', clave: 'ventas', ancho: 10 },
  { encabezado: 'Kg', clave: 'kilos', ancho: 12, formato: FORMATO_KG },
  { encabezado: 'Monto bruto', clave: 'montoBruto', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Monto neto', clave: 'montoNeto', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Precio prom/kg', clave: 'precioProm', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Precio mín/kg', clave: 'precioMin', ancho: 14, formato: FORMATO_MONTO },
  { encabezado: 'Precio máx/kg', clave: 'precioMax', ancho: 14, formato: FORMATO_MONTO },
  { encabezado: 'Clientes', clave: 'clientes', ancho: 12 },
  { encabezado: 'Participación % (monto)', clave: 'participacionMonto', ancho: 18, formato: FORMATO_PCT },
  { encabezado: 'Participación % (kilos)', clave: 'participacionKilos', ancho: 18, formato: FORMATO_PCT },
  { encabezado: 'Primera venta', clave: 'primeraVenta', ancho: 14, formato: FORMATO_FECHA },
  { encabezado: 'Última venta', clave: 'ultimaVenta', ancho: 14, formato: FORMATO_FECHA },
]

const COLUMNAS_CLIENTES = [
  { encabezado: 'Cliente', clave: 'cliente', ancho: 28 },
  { encabezado: 'Compras', clave: 'compras', ancho: 10 },
  { encabezado: 'Kg', clave: 'kilos', ancho: 12, formato: FORMATO_KG },
  { encabezado: 'Total comprado', clave: 'totalComprado', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Pagado', clave: 'pagado', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Saldo pendiente', clave: 'saldo', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Ticket promedio', clave: 'ticketProm', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Precio prom/kg', clave: 'precioProm', ancho: 14, formato: FORMATO_MONTO },
  { encabezado: 'Productos distintos', clave: 'productos', ancho: 16 },
  { encabezado: 'Cliente nuevo', clave: 'nuevo', ancho: 12 },
  { encabezado: 'Cliente recurrente', clave: 'recurrente', ancho: 14 },
  { encabezado: 'Primera compra', clave: 'primeraCompra', ancho: 14, formato: FORMATO_FECHA },
  { encabezado: 'Última compra', clave: 'ultimaCompra', ancho: 14, formato: FORMATO_FECHA },
  { encabezado: 'Días sin comprar', clave: 'diasSinComprar', ancho: 14 },
  { encabezado: 'Participación %', clave: 'participacion', ancho: 14, formato: FORMATO_PCT },
]

function agregarHojaTabular(libro, titulo, columnas, filas) {
  const hoja = libro.addWorksheet(titulo)

  hoja.columns = columnas.map((c) => ({ header: c.encabezado, key: c.clave, width: c.ancho || 16 }))
  hoja.getRow(1).font = { bold: true }

  filas.forEach((fila) => hoja.addRow(fila))

  columnas.forEach((columna, indice) => {
    if (columna.formato) {
      hoja.getColumn(indice + 1).numFmt = columna.formato
    }
  })

  if (columnas.length > 0) {
    hoja.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columnas.length } }
  }
}

// Un solo .xlsx con hojas Resumen / Productos / Clientes. Todos los
// valores numéricos (montos y kilos) se escriben como number real, nunca
// como texto formateado — así Excel puede sumar/ordenar cada columna.
export async function exportarAnaliticaPosExcel({ resumen, productos, clientes, filtrosTexto }, nombreArchivo) {
  const libro = new ExcelJS.Workbook()
  libro.creator = 'Franco Frutt Admin'
  libro.created = new Date()

  agregarHojaMetricas(libro, 'Resumen', construirFilasResumen(resumen, filtrosTexto))

  agregarHojaTabular(
    libro,
    'Productos',
    COLUMNAS_PRODUCTOS,
    productos.map((p) => ({
      producto: p.producto_nombre,
      codigo: p.producto_codigo,
      ventas: p.cantidad_ventas,
      kilos: Number(p.kilos_vendidos) || 0,
      montoBruto: Number(p.monto_bruto) || 0,
      montoNeto: Number(p.monto_neto) || 0,
      precioProm: Number(p.precio_promedio_kg) || 0,
      precioMin: Number(p.precio_minimo_kg) || 0,
      precioMax: Number(p.precio_maximo_kg) || 0,
      clientes: p.clientes_distintos,
      participacionMonto: Number(p.participacion_monto_pct) || 0,
      participacionKilos: Number(p.participacion_kilos_pct) || 0,
      primeraVenta: parsearFechaLocalPos(p.primera_venta),
      ultimaVenta: parsearFechaLocalPos(p.ultima_venta),
    }))
  )

  agregarHojaTabular(
    libro,
    'Clientes',
    COLUMNAS_CLIENTES,
    clientes.map((c) => ({
      cliente: c.razon_social,
      compras: c.cantidad_compras,
      kilos: Number(c.kilos_comprados) || 0,
      totalComprado: Number(c.total_comprado) || 0,
      pagado: Number(c.total_pagado) || 0,
      saldo: Number(c.saldo_pendiente) || 0,
      ticketProm: Number(c.ticket_promedio) || 0,
      precioProm: Number(c.precio_promedio_kg) || 0,
      productos: c.productos_distintos,
      nuevo: c.cliente_nuevo ? 'Sí' : 'No',
      recurrente: c.cliente_recurrente ? 'Sí' : 'No',
      primeraCompra: parsearFechaLocalPos(c.primera_compra_historica),
      ultimaCompra: parsearFechaLocalPos(c.ultima_compra_historica),
      diasSinComprar: c.dias_desde_ultima_compra,
      participacion: Number(c.participacion_pct) || 0,
    }))
  )

  const buffer = await libro.xlsx.writeBuffer()

  descargarBuffer(buffer, nombreArchivo)
}

// No usado directamente pero documentado: reutiliza formatearFechaPos
// para construir el texto de filtros que se guarda en la hoja Resumen.
export function construirTextoFiltrosAnalitica({ fechaDesde, fechaHasta, clienteNombre, productoNombre, estadoPago, usuarioNombre, tipoEnvaseNombre }) {
  const partes = [`${formatearFechaPos(fechaDesde)} a ${formatearFechaPos(fechaHasta)}`]

  if (clienteNombre) partes.push(`Cliente: ${clienteNombre}`)
  if (productoNombre) partes.push(`Producto: ${productoNombre}`)
  if (estadoPago) partes.push(`Estado pago: ${estadoPago}`)
  if (usuarioNombre) partes.push(`Usuario: ${usuarioNombre}`)
  if (tipoEnvaseNombre) partes.push(`Cajas/Rejillas: ${tipoEnvaseNombre}`)

  return partes.join(' | ')
}
